import { apiClient } from './apiClient';
import { TokenInterceptor } from './tokenInterceptor';
import { LoginRequest, RegisterRequest, AuthResponse, User, ChangePasswordRequest } from '../models/auth';

export const authService = {
  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await apiClient.post<LoginRequest, AuthResponse>('/auth/login', data);

    if (response.token) {
      apiClient.setAuthToken(response.token);
      this.storeToken(response.token);
      this.storeRefreshToken(response.refreshToken);
    }

    return response;
  },

  async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await apiClient.post<RegisterRequest, AuthResponse>('/auth/register', data);

    if (response.token) {
      apiClient.setAuthToken(response.token);
      this.storeToken(response.token);
      this.storeRefreshToken(response.refreshToken);
    }

    return response;
  },

  async logout(): Promise<void> {
    try {
      await apiClient.post<{}, void>('/auth/logout', {});
    } finally {
      apiClient.setAuthToken(null);
      this.removeToken();
      this.removeRefreshToken();
    }
  },

  async me(): Promise<User> {
    return apiClient.get<User>('/auth/me');
  },

  async getCurrentUser(): Promise<User> {
    return apiClient.get<User>('/auth/me');
  },

  async changePassword(data: ChangePasswordRequest): Promise<AuthResponse> {
    return apiClient.post<ChangePasswordRequest, AuthResponse>('/auth/change-password', data);
  },

  async refreshToken(): Promise<AuthResponse> {
    const success = await TokenInterceptor.handleUnauthorized();
    if (!success) {
      throw new Error('Token refresh failed');
    }

    const token = this.getStoredToken();
    const refreshToken = this.getStoredRefreshToken();
    
    if (!token || !refreshToken) {
      throw new Error('No tokens found after refresh');
    }

    return {
      token,
      refreshToken,
      user: JSON.parse(localStorage.getItem('current-user') || '{}')
    };
  },

  storeToken(token: string): void {
    localStorage.setItem('auth-token', token);
  },

  removeToken(): void {
    localStorage.removeItem('auth-token');
  },

  getStoredToken(): string | null {
    return localStorage.getItem('auth-token');
  },

  storeRefreshToken(refreshToken: string): void {
    localStorage.setItem('refresh-token', refreshToken);
  },

  getStoredRefreshToken(): string | null {
    return localStorage.getItem('refresh-token');
  },

  removeRefreshToken(): void {
    localStorage.removeItem('refresh-token');
  },

  initializeAuth(): void {
    const token = this.getStoredToken();
    if (token) {
      apiClient.setAuthToken(token);
    }
  },

  isAuthenticated(): boolean {
    return !!this.getStoredToken();
  }
};
